import { useExplorer } from "../../providers"

/**
 * Shared Explorer map chrome (slice E / SBP-8, #928) — the toolkit "Map
 * Controls" layout (PR #428, 3.166.0) rendered React-side by BOTH map
 * renderers: hide-map chevrons + the optional fullscreen button stacked on
 * the grid-adjacent top corner, zoom as a single +/− pill stack on the
 * opposite corner. The legacy home / previous / next marker-nav buttons are
 * retired. Corner sides mirror on map-left layouts (CSS). Tooltips render
 * from aria-label via the .hfse-mapctl-tip pattern — zoom stays bare, + and
 * − explain themselves. Below 960px only fullscreen renders visibly: the
 * stacked mobile map keeps pinch-zoom and the results-header text toggle.
 *
 * Unlike the module (which adopts and proxies core's DOM), the widget owns
 * its map state — the collapse button dispatches TOGGLE_MAP directly.
 */

const ICON_PROPS = {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: false,
} as const

/** Double chevron pointing INTO the map (»); CSS flips it on map-left. */
export function MapChevronsIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M7 17l5-5-5-5" />
      <path d="M14 17l5-5-5-5" />
    </svg>
  )
}

/** Folded-map glyph, shown beside the chevrons on the edge show-tab. */
export function MapGlyphIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M9 4.2 4 6.2v13.6l5-2 6 2 5-2V4.2l-5 2-6-2z" />
      <path d="M9 4.2v13.6" />
      <path d="M15 6.2v13.6" />
    </svg>
  )
}

type MapChromeProps = {
  onZoomIn: () => void
  onZoomOut: () => void
  onFullscreen: () => void
  showFullscreen: boolean
}

export default function MapChrome({ onZoomIn, onZoomOut, onFullscreen, showFullscreen }: MapChromeProps) {
  const { toggleMap } = useExplorer()
  return (
    <>
      <div className="hfse-mapctl-corner">
        <button
          type="button"
          className="hfse-mapctl-btn hfse-mapctl-collapse hfse-mapctl-tip"
          aria-label="Hide map"
          onClick={toggleMap}
        >
          <MapChevronsIcon />
        </button>
        {showFullscreen && (
          <button
            type="button"
            className="hfse-mapctl-btn hfse-mapctl-fullscreen hfse-mapctl-tip"
            aria-label="Fullscreen"
            onClick={onFullscreen}
          >
            <svg {...ICON_PROPS} strokeWidth={2.5}>
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </button>
        )}
      </div>

      <div className="hfse-mapctl-zoom">
        <button type="button" className="hfse-mapctl-btn" aria-label="Zoom in" onClick={onZoomIn}>
          <svg {...ICON_PROPS}>
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
        </button>
        <button type="button" className="hfse-mapctl-btn" aria-label="Zoom out" onClick={onZoomOut}>
          <svg {...ICON_PROPS}>
            <path d="M5 12h14" />
          </svg>
        </button>
      </div>
    </>
  )
}
